document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const Nordenly = {
  init() {
    this.mobileNav();
    this.disclosure();
    this.productGallery();
    this.productCarousels();
    this.collectionFilters();
    this.popup();
  },

  mobileNav() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const drawer = document.querySelector('[data-mobile-drawer]');
    const close = document.querySelector('[data-menu-close]');
    if (!toggle || !drawer) return;

    const setOpen = (open) => {
      drawer.hidden = !open;
      document.body.classList.toggle('drawer-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    };

    toggle.addEventListener('click', () => setOpen(drawer.hidden));
    close?.addEventListener('click', () => setOpen(false));
    drawer.addEventListener('click', (event) => {
      if (event.target.matches('[data-drawer-overlay]')) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });
  },

  disclosure() {
    document.querySelectorAll('[data-disclosure-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const target = document.getElementById(trigger.getAttribute('aria-controls'));
        if (!target) return;
        const open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!open));
        target.hidden = open;
        if (trigger.dataset.labelOpen && trigger.dataset.labelClose) {
          trigger.setAttribute('aria-label', open ? trigger.dataset.labelOpen : trigger.dataset.labelClose);
        }
      });
    });
  },

  productGallery() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      if (root.dataset.productInitialized === 'true') return;
      root.dataset.productInitialized = 'true';

      const media = [...root.querySelectorAll('[data-product-media]')];
      const thumbnails = [...root.querySelectorAll('[data-product-thumb]')];
      const mediaList = root.querySelector('[data-product-media-list]');
      const currentCount = root.querySelector('[data-gallery-current]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const mobileGallery = window.matchMedia('(max-width: 991.98px)');
      let currentIndex = Math.max(0, media.findIndex((item) => !item.hidden));

      const pauseMedia = (item) => {
        item.querySelectorAll('video').forEach((video) => video.pause());
        item.querySelectorAll('iframe').forEach((frame) => {
          const source = frame.src;
          frame.src = '';
          frame.src = source;
        });
      };

      const activateMedia = (index, scrollOnMobile = false) => {
        if (!media.length) return;
        currentIndex = (index + media.length) % media.length;
        media.forEach((item, itemIndex) => {
          item.hidden = itemIndex !== currentIndex;
          if (itemIndex !== currentIndex) pauseMedia(item);
        });
        thumbnails.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === currentIndex;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', String(active));
        });
        if (currentCount) currentCount.textContent = String(currentIndex + 1);
        thumbnails[currentIndex]?.scrollIntoView({ block: 'nearest' });
        if (scrollOnMobile && mobileGallery.matches && mediaList) {
          mediaList.scrollTo({
            left: media[currentIndex].offsetLeft,
            behavior: reducedMotion.matches ? 'auto' : 'smooth'
          });
        }
      };

      thumbnails.forEach((thumb, index) => {
        thumb.addEventListener('click', () => activateMedia(index));
      });
      root.querySelector('[data-gallery-previous]')?.addEventListener('click', () => activateMedia(currentIndex - 1, true));
      root.querySelector('[data-gallery-next]')?.addEventListener('click', () => activateMedia(currentIndex + 1, true));

      if (mediaList && media.length > 1 && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible || !mobileGallery.matches) return;
          const index = media.indexOf(visible.target);
          if (index >= 0) {
            currentIndex = index;
            if (currentCount) currentCount.textContent = String(index + 1);
          }
        }, { root: mediaList, threshold: [.6, .8] });
        media.forEach((item) => observer.observe(item));
      }

      const quantity = root.querySelector('input[name="quantity"]');
      const changeQuantity = (direction) => {
        if (!quantity) return;
        const step = Number(quantity.step || 1);
        const minimum = Number(quantity.min || 1);
        const maximum = quantity.max ? Number(quantity.max) : Number.POSITIVE_INFINITY;
        const next = Math.min(maximum, Math.max(minimum, Number(quantity.value || minimum) + (step * direction)));
        quantity.value = String(next);
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      };
      root.querySelector('[data-quantity-decrease]')?.addEventListener('click', () => changeQuantity(-1));
      root.querySelector('[data-quantity-increase]')?.addEventListener('click', () => changeQuantity(1));

      const variantSelect = root.querySelector('[data-product-variant-select]');
      const updateVariant = ({ updateUrl = true } = {}) => {
        const option = variantSelect?.selectedOptions[0];
        if (!option) return;
        const available = option.dataset.available === 'true';
        const price = option.dataset.price || '';
        const comparePrice = option.dataset.comparePrice || '';
        const priceElement = root.querySelector('[data-product-price]');
        const compareElement = root.querySelector('[data-product-compare-price]');
        const stockElement = root.querySelector('[data-product-stock]');
        const skuElement = root.querySelector('[data-product-sku]');
        const skuRow = root.querySelector('[data-product-sku-row]');

        if (priceElement) priceElement.textContent = price;
        if (compareElement) {
          compareElement.textContent = comparePrice;
          compareElement.hidden = !comparePrice;
        }
        root.querySelectorAll('[data-mobile-product-price]').forEach((item) => { item.textContent = price; });
        if (stockElement) {
          stockElement.textContent = available ? 'In stock' : 'Currently unavailable';
          stockElement.classList.toggle('is-unavailable', !available);
        }
        root.querySelectorAll('[data-product-add], [data-mobile-product-add]').forEach((button) => {
          button.disabled = !available;
          button.textContent = available ? 'Add to cart' : 'Sold out';
        });
        if (skuElement) skuElement.textContent = option.dataset.sku || '';
        if (skuRow) skuRow.hidden = !option.dataset.sku;
        if (quantity) {
          const minimum = Number(option.dataset.quantityMin || 1);
          const increment = Number(option.dataset.quantityIncrement || 1);
          const maximum = option.dataset.quantityMax ? Number(option.dataset.quantityMax) : null;
          quantity.min = String(minimum);
          quantity.step = String(increment);
          if (maximum) quantity.max = String(maximum);
          else quantity.removeAttribute('max');
          const current = Number(quantity.value || minimum);
          const aligned = minimum + (Math.max(0, Math.ceil((current - minimum) / increment)) * increment);
          quantity.value = String(Math.min(maximum || Number.POSITIVE_INFINITY, Math.max(minimum, aligned)));
        }

        if (option.dataset.mediaId) {
          const mediaIndex = media.findIndex((item) => item.dataset.mediaId === option.dataset.mediaId);
          if (mediaIndex >= 0) activateMedia(mediaIndex, true);
        }
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', option.value);
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }
      };
      variantSelect?.addEventListener('change', updateVariant);
      if (variantSelect) updateVariant({ updateUrl: false });

      mobileGallery.addEventListener?.('change', (event) => {
        if (!event.matches) activateMedia(currentIndex);
      });

      const mainAdd = root.querySelector('[data-product-add]');
      root.querySelector('[data-mobile-product-add]')?.addEventListener('click', () => {
        if (!mainAdd?.disabled) mainAdd.click();
      });
    });
  },

  productCarousels() {
    document.querySelectorAll('.featured-collection').forEach((section) => {
      const carousel = section.querySelector('[data-product-carousel]');
      const previous = section.querySelector('[data-carousel-prev]');
      const next = section.querySelector('[data-carousel-next]');
      if (!carousel || !previous || !next) return;

      const move = (direction) => {
        carousel.scrollBy({
          left: carousel.clientWidth * .82 * direction,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
    });
  },

  collectionFilters() {
    document.querySelectorAll('[data-filter-toggle]').forEach((toggle) => {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      const media = window.matchMedia('(max-width: 780px)');

      const setOpen = (open) => {
        panel.hidden = !open;
        toggle.setAttribute('aria-expanded', String(open));
        const indicator = toggle.querySelector('[aria-hidden="true"]');
        if (indicator) indicator.textContent = open ? '−' : '+';
      };

      const sync = () => setOpen(!media.matches);
      toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
      media.addEventListener?.('change', sync);
      sync();
    });
  },

  popup() {
    const popup = document.querySelector('[data-newsletter-popup]');
    if (!popup || sessionStorage.getItem('nordenly-popup-seen')) return;
    const delay = Number(popup.dataset.delay || 7000);
    const close = () => {
      popup.hidden = true;
      sessionStorage.setItem('nordenly-popup-seen', 'true');
    };
    setTimeout(() => { popup.hidden = false; }, delay);
    popup.querySelectorAll('[data-popup-close]').forEach((button) => button.addEventListener('click', close));
    popup.addEventListener('click', (event) => {
      if (event.target === popup) close();
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => Nordenly.init());
} else {
  Nordenly.init();
}

document.addEventListener('shopify:section:load', () => Nordenly.productGallery());
